import React, { useState, useEffect, useRef } from 'react';
import { getApiUrl } from '../apiConfig';

export default function ProcurementManager({ onSubmitting }) {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [editingId, setEditingId] = useState(null);
    const [message, setMessage] = useState('');

    const fileInputRef = useRef(null);

    // Form state
    const [form, setForm] = useState({
        title: '',
        date: new Date().toISOString().split('T')[0],
        fileType: 'pdf', // 'pdf' or 'image'
        fileData: '',
        fileName: '',
        fileSize: '',
        description: '',
        status: 'active' // 'active' or 'closed'
    });

    // Helper: open PDF or image in a new tab safely (supports Base64 data URIs)
    const openFileInNewTab = (fileData, fileType, title = 'Document') => {
        if (!fileData) return;
        try {
            if (fileData.startsWith('data:application/pdf')) {
                // Convert Base64 to Blob URL for clean PDF viewing in browser
                const base64Parts = fileData.split(',');
                const byteCharacters = atob(base64Parts[1]);
                const byteNumbers = new Array(byteCharacters.length);
                for (let i = 0; i < byteCharacters.length; i++) {
                    byteNumbers[i] = byteCharacters.charCodeAt(i);
                }
                const byteArray = new Uint8Array(byteNumbers);
                const blob = new Blob([byteArray], { type: 'application/pdf' });
                const blobUrl = URL.createObjectURL(blob);
                window.open(blobUrl, '_blank');
            } else if (fileData.startsWith('data:image')) {
                const win = window.open('', '_blank');
                if (win) {
                    win.document.write(`
                        <html>
                            <head><title>${title}</title><style>body{margin:0;background:#111;display:flex;justify-content:center;align-items:center;min-height:100vh;}img{max-width:95vw;max-height:95vh;box-shadow:0 8px 32px rgba(0,0,0,0.5);border-radius:6px;}</style></head>
                            <body><img src="${fileData}" alt="${title}" /></body>
                        </html>
                    `);
                    win.document.close();
                }
            } else {
                // Normal HTTP URL
                window.open(fileData, '_blank');
            }
        } catch (err) {
            console.error('Error opening file:', err);
            // Fallback direct open
            window.open(fileData, '_blank');
        }
    };

    // Fetch initial procurement data
    const fetchProcurement = async () => {
        setLoading(true);
        try {
            const url = getApiUrl(`api_procurement.php?t=${Date.now()}`);
            const res = await fetch(url);
            if (res.ok) {
                const data = await res.json();
                if (Array.isArray(data)) {
                    setItems(data);
                    try { localStorage.setItem('site_procurement', JSON.stringify(data)); } catch (e) {}
                    return;
                }
            }
            throw new Error('API fetch failed');
        } catch (err) {
            console.warn('Procurement API failed, falling back to local files...', err);
            // Try static procurement_data.json
            try {
                const fallbackRes = await fetch(`/procurement_data.json?t=${Date.now()}`);
                if (fallbackRes.ok) {
                    const fallbackData = await fallbackRes.json();
                    if (Array.isArray(fallbackData)) {
                        setItems(fallbackData);
                        return;
                    }
                }
            } catch (fbErr) {
                console.warn('Static json fallback failed:', fbErr);
            }

            // LocalStorage fallback
            const local = localStorage.getItem('site_procurement');
            if (local) {
                try {
                    setItems(JSON.parse(local));
                } catch (parseErr) {
                    setItems([]);
                }
            }
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchProcurement();
    }, []);

    // Handle File Input Change (Image or PDF)
    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        // Check file size (e.g. 15MB limit)
        if (file.size > 15 * 1024 * 1024) {
            alert('File is too large. Please select a file smaller than 15MB.');
            if (fileInputRef.current) fileInputRef.current.value = '';
            return;
        }

        const sizeFormatted = file.size > 1024 * 1024
            ? `${(file.size / (1024 * 1024)).toFixed(2)} MB`
            : `${(file.size / 1024).toFixed(0)} KB`;

        const isPdf = file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');
        const isImg = file.type.startsWith('image/');

        if (form.fileType === 'pdf' && !isPdf) {
            alert('කරුණාකර PDF ලේඛනයක් තෝරන්න (Please select a PDF file).');
            if (fileInputRef.current) fileInputRef.current.value = '';
            return;
        }

        if (form.fileType === 'image' && !isImg) {
            alert('කරුණාකර ඡායාරූපයක් තෝරන්න (Please select an image file).');
            if (fileInputRef.current) fileInputRef.current.value = '';
            return;
        }

        const reader = new FileReader();
        reader.onload = (event) => {
            setForm(prev => ({
                ...prev,
                fileData: event.target.result,
                fileName: file.name,
                fileSize: sizeFormatted
            }));
        };
        reader.readAsDataURL(file);
    };

    // Save or Update Item
    const handleSubmit = async (e) => {
        if (e) {
            e.preventDefault();
            e.stopPropagation();
        }

        if (!form.title.trim()) {
            alert('කරුණාකර ශීර්ෂය (Heading) ඇතුලත් කරන්න.');
            return;
        }

        if (!form.fileData) {
            alert(form.fileType === 'pdf' 
                ? 'කරුණාකර PDF ලේඛනයක් තෝරන්න (Please upload a PDF).' 
                : 'කරුණාකර ඡායාරූපයක් තෝරන්න (Please upload an image).');
            return;
        }

        if (onSubmitting) onSubmitting(true);
        setMessage('');

        try {
            const id = editingId || `proc-${Date.now()}`;
            const newItem = {
                id,
                title: form.title.trim(),
                date: form.date || new Date().toISOString().split('T')[0],
                fileType: form.fileType,
                fileData: form.fileData,
                fileName: form.fileName || (form.fileType === 'pdf' ? 'document.pdf' : 'image.jpg'),
                fileSize: form.fileSize || '',
                description: form.description.trim(),
                status: form.status || 'active'
            };

            const updatedItems = editingId
                ? items.map(item => item.id === editingId ? newItem : item)
                : [newItem, ...items];

            setItems(updatedItems);

            // LocalStorage sync
            try {
                localStorage.setItem('site_procurement', JSON.stringify(updatedItems));
            } catch (lsErr) {
                console.warn('LocalStorage save failed:', lsErr);
            }

            // Server sync via PHP API
            try {
                const response = await fetch(getApiUrl('api_procurement.php'), {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(updatedItems)
                });

                if (response.ok) {
                    setMessage(editingId ? '✅ ප්‍රසම්පාදන විස්තර සාර්ථකව යාවත්කාලීන විය!' : '✅ නව ප්‍රසම්පාදන දැන්වීම සාර්ථකව ඇතුලත් විය!');
                } else {
                    throw new Error('API server sync response not ok');
                }
            } catch (apiErr) {
                console.error('Server sync failed:', apiErr);
                setMessage('⚠ විස්තර Local Storage හි සුරැකිණි. සර්වර් වෙත sync වීම ප්‍රමාද විය හැක.');
            }

            // Reset form
            handleCancelEdit();
            window.scrollTo({ top: 0, behavior: 'smooth' });
            setTimeout(() => setMessage(''), 4500);

        } catch (err) {
            console.error('Submit error:', err);
            setMessage('⚠ Error: ' + err.message);
        } finally {
            if (onSubmitting) onSubmitting(false);
        }
    };

    // Load item to edit
    const handleEdit = (item) => {
        setEditingId(item.id);
        setForm({
            title: item.title || '',
            date: item.date || new Date().toISOString().split('T')[0],
            fileType: item.fileType || (item.fileData?.includes('pdf') ? 'pdf' : 'image'),
            fileData: item.fileData || '',
            fileName: item.fileName || '',
            fileSize: item.fileSize || '',
            description: item.description || '',
            status: item.status || 'active'
        });
        if (fileInputRef.current) fileInputRef.current.value = '';
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    // Cancel edit
    const handleCancelEdit = () => {
        setEditingId(null);
        setForm({
            title: '',
            date: new Date().toISOString().split('T')[0],
            fileType: 'pdf',
            fileData: '',
            fileName: '',
            fileSize: '',
            description: '',
            status: 'active'
        });
        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    // Delete item
    const handleDelete = async (id) => {
        const itemToDelete = items.find(i => i.id === id);
        const confirmMsg = `ඔබට මෙම ප්‍රසම්පාදන දැන්වීම ඉවත් කිරීමට අවශ්‍ය ද?\n"${itemToDelete?.title || 'මෙම ලේඛනය'}"`;
        if (!window.confirm(confirmMsg)) return;

        if (onSubmitting) onSubmitting(true);
        const updatedItems = items.filter(item => item.id !== id);
        setItems(updatedItems);

        try {
            localStorage.setItem('site_procurement', JSON.stringify(updatedItems));
        } catch (e) {}

        try {
            await fetch(getApiUrl('api_procurement.php'), {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(updatedItems)
            });
            setMessage('🗑️ ප්‍රසම්පාදන දැන්වීම සාර්ථකව ඉවත් කරන ලදී.');
            setTimeout(() => setMessage(''), 3500);
        } catch (err) {
            console.error('Delete sync failed:', err);
        } finally {
            if (onSubmitting) onSubmitting(false);
        }
    };

    // Switch File Type (Image <-> PDF)
    const handleFileTypeSwitch = (type) => {
        if (form.fileType === type) return;
        setForm(prev => ({
            ...prev,
            fileType: type,
            fileData: '',
            fileName: '',
            fileSize: ''
        }));
        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    // Filter items
    const filteredItems = items.filter(item => {
        const matchTitle = item.title?.toLowerCase().includes(searchTerm.toLowerCase());
        const matchDate = item.date?.toLowerCase().includes(searchTerm.toLowerCase());
        const matchDesc = item.description?.toLowerCase().includes(searchTerm.toLowerCase());
        return matchTitle || matchDate || matchDesc;
    });

    return (
        <div className="procurement-manager-container" style={{ padding: '10px 0' }}>
            {/* Header info banner */}
            <div style={{
                background: 'linear-gradient(135deg, #0e633d 0%, #168052 100%)',
                color: 'white',
                padding: '24px 28px',
                borderRadius: '14px',
                marginBottom: '24px',
                boxShadow: '0 4px 18px rgba(14, 99, 61, 0.15)',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: '15px'
            }}>
                <div>
                    <h2 style={{ margin: '0 0 6px 0', fontSize: '24px', fontWeight: '700', letterSpacing: '-0.3px' }}>
                        📑 ප්‍රසම්පාදන කළමනාකරණය (Procurement / Tenders)
                    </h2>
                    <p style={{ margin: 0, opacity: 0.92, fontSize: '14px' }}>
                        ටෙන්ඩර්, මිල කැඳවීම් සහ ප්‍රසම්පාදන දැන්වීම් Image හෝ PDF මාධ්‍යයෙන් වෙබ් අඩවියට ඇතුලත් කර කළමනාකරණය කරන්න.
                    </p>
                </div>
                <div style={{
                    background: 'rgba(255, 255, 255, 0.18)',
                    backdropFilter: 'blur(8px)',
                    padding: '10px 18px',
                    borderRadius: '10px',
                    textAlign: 'center'
                }}>
                    <span style={{ display: 'block', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>මුළු දැන්වීම්</span>
                    <strong style={{ fontSize: '22px' }}>{items.length}</strong>
                </div>
            </div>

            {/* Notification alert */}
            {message && (
                <div style={{
                    padding: '14px 20px',
                    borderRadius: '10px',
                    marginBottom: '20px',
                    fontWeight: '600',
                    fontSize: '15px',
                    backgroundColor: message.includes('⚠') ? '#fff3cd' : '#d1e7dd',
                    color: message.includes('⚠') ? '#856404' : '#0f5132',
                    border: message.includes('⚠') ? '1px solid #ffeeba' : '1px solid #badbcc',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
                }}>
                    <span>{message}</span>
                    <button 
                        onClick={() => setMessage('')} 
                        style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '18px', fontWeight: 'bold' }}
                    >
                        ×
                    </button>
                </div>
            )}

            {/* Main Form Section */}
            <div style={{
                background: '#ffffff',
                borderRadius: '14px',
                padding: '28px',
                marginBottom: '30px',
                boxShadow: '0 3px 14px rgba(0, 0, 0, 0.06)',
                border: editingId ? '2px solid #0e633d' : '1px solid #e2e8f0'
            }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '22px', borderBottom: '1px solid #eef2f6', paddingBottom: '14px' }}>
                    <h3 style={{ margin: 0, fontSize: '19px', color: '#1e293b', display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span>{editingId ? '✏️ දැන්වීම යාවත්කාලීන කරන්න' : '➕ නව ප්‍රසම්පාදන දැන්වීමක් එක් කරන්න'}</span>
                    </h3>
                    {editingId && (
                        <button
                            type="button"
                            onClick={handleCancelEdit}
                            style={{
                                background: '#f1f5f9',
                                color: '#475569',
                                border: '1px solid #cbd5e1',
                                padding: '6px 14px',
                                borderRadius: '6px',
                                cursor: 'pointer',
                                fontSize: '13px',
                                fontWeight: '600'
                            }}
                        >
                            ✕ අවලංගු කරන්න (Cancel)
                        </button>
                    )}
                </div>

                <form onSubmit={handleSubmit}>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '20px', marginBottom: '20px' }}>
                        {/* Heading / Title */}
                        <div style={{ gridColumn: '1 / -1' }}>
                            <label style={{ display: 'block', fontWeight: '600', color: '#334155', marginBottom: '6px', fontSize: '14px' }}>
                                ශීර්ෂය / මාතෘකාව (Heading / Tender Title) <span style={{ color: '#dc2626' }}>*</span>
                            </label>
                            <input
                                type="text"
                                value={form.title}
                                onChange={(e) => setForm({ ...form, title: e.target.value })}
                                placeholder="උදා: සබරගමුව පළාත තුළ කෘෂිකාර්මික උපකරණ මිලදී ගැනීම සඳහා මිල කැඳවීම"
                                required
                                style={{
                                    width: '100%',
                                    padding: '12px 14px',
                                    borderRadius: '8px',
                                    border: '1.5px solid #cbd5e1',
                                    fontSize: '15px',
                                    outline: 'none',
                                    transition: 'border-color 0.2s',
                                    boxSizing: 'border-box'
                                }}
                                onFocus={(e) => e.target.style.borderColor = '#0e633d'}
                                onBlur={(e) => e.target.style.borderColor = '#cbd5e1'}
                            />
                        </div>

                        {/* Date */}
                        <div>
                            <label style={{ display: 'block', fontWeight: '600', color: '#334155', marginBottom: '6px', fontSize: '14px' }}>
                                දිනය (Date) <span style={{ color: '#dc2626' }}>*</span>
                            </label>
                            <input
                                type="date"
                                value={form.date}
                                onChange={(e) => setForm({ ...form, date: e.target.value })}
                                required
                                style={{
                                    width: '100%',
                                    padding: '12px 14px',
                                    borderRadius: '8px',
                                    border: '1.5px solid #cbd5e1',
                                    fontSize: '15px',
                                    outline: 'none',
                                    boxSizing: 'border-box'
                                }}
                            />
                        </div>

                        {/* Status */}
                        <div>
                            <label style={{ display: 'block', fontWeight: '600', color: '#334155', marginBottom: '6px', fontSize: '14px' }}>
                                තත්ත්වය (Status)
                            </label>
                            <select
                                value={form.status}
                                onChange={(e) => setForm({ ...form, status: e.target.value })}
                                style={{
                                    width: '100%',
                                    padding: '12px 14px',
                                    borderRadius: '8px',
                                    border: '1.5px solid #cbd5e1',
                                    fontSize: '15px',
                                    outline: 'none',
                                    boxSizing: 'border-box',
                                    background: 'white'
                                }}
                            >
                                <option value="active">🟢 සක්‍රියයි (Active / Open)</option>
                                <option value="closed">🔴 අවසන් වී ඇත (Closed / Expired)</option>
                            </select>
                        </div>
                    </div>

                    {/* FILE TYPE SELECTION (IMAGE OR PDF) */}
                    <div style={{
                        background: '#f8fafc',
                        border: '1.5px dashed #cbd5e1',
                        borderRadius: '12px',
                        padding: '22px',
                        marginBottom: '22px'
                    }}>
                        <label style={{ display: 'block', fontWeight: '700', color: '#1e293b', marginBottom: '12px', fontSize: '15px' }}>
                            ලේඛන මාධ්‍ය තෝරන්න (Select Media Type: Image OR PDF) <span style={{ color: '#dc2626' }}>*</span>
                        </label>

                        {/* Toggle Buttons */}
                        <div style={{ display: 'flex', gap: '15px', marginBottom: '18px', flexWrap: 'wrap' }}>
                            <button
                                type="button"
                                onClick={() => handleFileTypeSwitch('pdf')}
                                style={{
                                    flex: '1',
                                    minWidth: '180px',
                                    padding: '14px 18px',
                                    borderRadius: '10px',
                                    border: form.fileType === 'pdf' ? '2px solid #0e633d' : '1.5px solid #e2e8f0',
                                    background: form.fileType === 'pdf' ? '#ecfdf5' : '#ffffff',
                                    color: form.fileType === 'pdf' ? '#065f46' : '#64748b',
                                    fontWeight: '700',
                                    fontSize: '15px',
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: '10px',
                                    transition: 'all 0.2s',
                                    boxShadow: form.fileType === 'pdf' ? '0 3px 10px rgba(14, 99, 61, 0.12)' : 'none'
                                }}
                            >
                                <span style={{ fontSize: '22px' }}>📄</span>
                                <span>PDF ලේඛනයක් (PDF Document)</span>
                            </button>

                            <button
                                type="button"
                                onClick={() => handleFileTypeSwitch('image')}
                                style={{
                                    flex: '1',
                                    minWidth: '180px',
                                    padding: '14px 18px',
                                    borderRadius: '10px',
                                    border: form.fileType === 'image' ? '2px solid #0e633d' : '1.5px solid #e2e8f0',
                                    background: form.fileType === 'image' ? '#ecfdf5' : '#ffffff',
                                    color: form.fileType === 'image' ? '#065f46' : '#64748b',
                                    fontWeight: '700',
                                    fontSize: '15px',
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: '10px',
                                    transition: 'all 0.2s',
                                    boxShadow: form.fileType === 'image' ? '0 3px 10px rgba(14, 99, 61, 0.12)' : 'none'
                                }}
                            >
                                <span style={{ fontSize: '22px' }}>🖼️</span>
                                <span>ඡායාරූපයක් (Image File)</span>
                            </button>
                        </div>

                        {/* File Upload Input */}
                        <div style={{ marginBottom: '14px' }}>
                            <label style={{ display: 'block', fontSize: '13px', color: '#475569', marginBottom: '8px', fontWeight: '600' }}>
                                {form.fileType === 'pdf' ? 'පීඩීඑෆ් ගොනුව උඩුගත කරන්න (.pdf):' : 'ඡායාරූප ගොනුව උඩුගත කරන්න (.jpg, .jpeg, .png):'}
                            </label>
                            <input
                                ref={fileInputRef}
                                type="file"
                                accept={form.fileType === 'pdf' ? '.pdf,application/pdf' : 'image/*'}
                                onChange={handleFileChange}
                                style={{
                                    display: 'block',
                                    width: '100%',
                                    padding: '10px',
                                    fontSize: '14px',
                                    background: '#ffffff',
                                    borderRadius: '8px',
                                    border: '1px solid #cbd5e1',
                                    boxSizing: 'border-box'
                                }}
                            />
                        </div>

                        {/* PREVIEW CONTAINER */}
                        {form.fileData && (
                            <div style={{
                                marginTop: '16px',
                                padding: '14px 18px',
                                background: '#ffffff',
                                borderRadius: '10px',
                                border: '1px solid #e2e8f0',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                flexWrap: 'wrap',
                                gap: '14px'
                            }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                                    {form.fileType === 'pdf' ? (
                                        <div style={{
                                            width: '46px',
                                            height: '46px',
                                            background: '#fee2e2',
                                            color: '#ef4444',
                                            borderRadius: '8px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            fontSize: '22px'
                                        }}>
                                            📄
                                        </div>
                                    ) : (
                                        <img
                                            src={form.fileData}
                                            alt="Preview"
                                            style={{
                                                width: '60px',
                                                height: '60px',
                                                objectFit: 'cover',
                                                borderRadius: '8px',
                                                border: '1px solid #e2e8f0'
                                            }}
                                        />
                                    )}
                                    <div>
                                        <div style={{ fontWeight: '600', color: '#1e293b', fontSize: '14px' }}>
                                            {form.fileName || (form.fileType === 'pdf' ? 'Uploaded Document.pdf' : 'Uploaded Image')}
                                        </div>
                                        {form.fileSize && (
                                            <div style={{ fontSize: '12px', color: '#64748b' }}>
                                                ප්‍රමාණය: {form.fileSize}
                                            </div>
                                        )}
                                        <span style={{
                                            display: 'inline-block',
                                            fontSize: '11px',
                                            background: form.fileType === 'pdf' ? '#fee2e2' : '#e0e7ff',
                                            color: form.fileType === 'pdf' ? '#b91c1c' : '#4338ca',
                                            padding: '2px 8px',
                                            borderRadius: '4px',
                                            fontWeight: '700',
                                            marginTop: '4px'
                                        }}>
                                            {form.fileType.toUpperCase()}
                                        </span>
                                    </div>
                                </div>

                                <div style={{ display: 'flex', gap: '8px' }}>
                                    <button
                                        type="button"
                                        onClick={() => openFileInNewTab(form.fileData, form.fileType, form.title)}
                                        style={{
                                            background: '#0e633d',
                                            color: 'white',
                                            border: 'none',
                                            padding: '8px 14px',
                                            borderRadius: '6px',
                                            fontSize: '13px',
                                            fontWeight: '600',
                                            cursor: 'pointer',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '6px'
                                        }}
                                    >
                                        <span>👁️</span> විවෘත කර බලන්න (Test Open)
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setForm(prev => ({ ...prev, fileData: '', fileName: '', fileSize: '' }));
                                            if (fileInputRef.current) fileInputRef.current.value = '';
                                        }}
                                        style={{
                                            background: '#fef2f2',
                                            color: '#ef4444',
                                            border: '1px solid #fecaca',
                                            padding: '8px 12px',
                                            borderRadius: '6px',
                                            fontSize: '13px',
                                            cursor: 'pointer'
                                        }}
                                        title="Remove file"
                                    >
                                        ඉවත් කරන්න
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Optional Description / Notes */}
                    <div style={{ marginBottom: '22px' }}>
                        <label style={{ display: 'block', fontWeight: '600', color: '#334155', marginBottom: '6px', fontSize: '14px' }}>
                            අමතර විස්තර / උපදෙස් (Description / Instructions - Optional)
                        </label>
                        <textarea
                            rows="3"
                            value={form.description}
                            onChange={(e) => setForm({ ...form, description: e.target.value })}
                            placeholder="ටෙන්ඩරය භාරගන්නා අවසන් දිනය හෝ අදාල කාර්යාලය පිළිබඳ කෙටි සටහනක්..."
                            style={{
                                width: '100%',
                                padding: '12px 14px',
                                borderRadius: '8px',
                                border: '1.5px solid #cbd5e1',
                                fontSize: '14px',
                                outline: 'none',
                                resize: 'vertical',
                                boxSizing: 'border-box'
                            }}
                        />
                    </div>

                    {/* Submit Buttons */}
                    <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                        <button
                            type="submit"
                            style={{
                                background: '#0e633d',
                                color: 'white',
                                border: 'none',
                                padding: '12px 28px',
                                borderRadius: '8px',
                                fontSize: '15px',
                                fontWeight: '700',
                                cursor: 'pointer',
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '8px',
                                boxShadow: '0 3px 12px rgba(14, 99, 61, 0.25)',
                                transition: 'all 0.2s'
                            }}
                        >
                            <span>{editingId ? '💾 යාවත්කාලීන කරන්න (Update)' : '➕ දැන්වීම සුරකින්න (Publish Notice)'}</span>
                        </button>
                        {editingId && (
                            <button
                                type="button"
                                onClick={handleCancelEdit}
                                style={{
                                    background: '#f1f5f9',
                                    color: '#475569',
                                    border: '1px solid #cbd5e1',
                                    padding: '12px 20px',
                                    borderRadius: '8px',
                                    fontSize: '15px',
                                    fontWeight: '600',
                                    cursor: 'pointer'
                                }}
                            >
                                අවලංගු කරන්න
                            </button>
                        )}
                    </div>
                </form>
            </div>

            {/* List / Table Section */}
            <div style={{
                background: '#ffffff',
                borderRadius: '14px',
                padding: '24px',
                boxShadow: '0 3px 14px rgba(0, 0, 0, 0.06)',
                border: '1px solid #e2e8f0'
            }}>
                <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '18px',
                    flexWrap: 'wrap',
                    gap: '14px'
                }}>
                    <h3 style={{ margin: 0, fontSize: '18px', color: '#1e293b' }}>
                        📋 ඇතුලත් කර ඇති ප්‍රසම්පාදන දැන්වීම් ({filteredItems.length})
                    </h3>
                    <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                        <input
                            type="text"
                            placeholder="🔍 සොයන්න (Search title or date)..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            style={{
                                padding: '8px 14px',
                                borderRadius: '6px',
                                border: '1px solid #cbd5e1',
                                fontSize: '14px',
                                minWidth: '240px',
                                outline: 'none'
                            }}
                        />
                        <button
                            type="button"
                            onClick={fetchProcurement}
                            title="Refresh list"
                            style={{
                                padding: '8px 12px',
                                background: '#f8fafc',
                                border: '1px solid #cbd5e1',
                                borderRadius: '6px',
                                cursor: 'pointer',
                                fontSize: '14px'
                            }}
                        >
                            🔄
                        </button>
                    </div>
                </div>

                {loading ? (
                    <div style={{ textAlign: 'center', padding: '40px', color: '#64748b' }}>
                        දත්ත ලබාගනිමින් පවතී (Loading procurement data)...
                    </div>
                ) : filteredItems.length === 0 ? (
                    <div style={{
                        textAlign: 'center',
                        padding: '40px 20px',
                        background: '#f8fafc',
                        borderRadius: '10px',
                        color: '#64748b'
                    }}>
                        {searchTerm ? 'සෙවුමට අදාල දැන්වීම් කිසිවක් හමු නොවීය.' : 'තවමත් ප්‍රසම්පාදන දැන්වීම් ඇතුලත් කර නොමැත.'}
                    </div>
                ) : (
                    <div style={{ overflowX: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: '14px' }}>
                            <thead>
                                <tr style={{ background: '#f8fafc', borderBottom: '2px solid #e2e8f0', color: '#475569' }}>
                                    <th style={{ padding: '12px 14px', width: '90px' }}>වර්ගය</th>
                                    <th style={{ padding: '12px 14px' }}>ශීර්ෂය (Heading)</th>
                                    <th style={{ padding: '12px 14px', width: '120px' }}>දිනය</th>
                                    <th style={{ padding: '12px 14px', width: '110px' }}>තත්ත්වය</th>
                                    <th style={{ padding: '12px 14px', width: '140px' }}>ලේඛනය (File)</th>
                                    <th style={{ padding: '12px 14px', width: '150px', textAlign: 'right' }}>ක්‍රියාමාර්ග</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredItems.map(item => (
                                    <tr
                                        key={item.id}
                                        style={{
                                            borderBottom: '1px solid #f1f5f9',
                                            backgroundColor: editingId === item.id ? '#f0fdf4' : 'transparent',
                                            transition: 'background-color 0.15s'
                                        }}
                                    >
                                        {/* Type badge */}
                                        <td style={{ padding: '12px 14px' }}>
                                            <span style={{
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                gap: '4px',
                                                padding: '4px 8px',
                                                borderRadius: '6px',
                                                fontSize: '12px',
                                                fontWeight: '700',
                                                background: item.fileType === 'pdf' ? '#fee2e2' : '#e0e7ff',
                                                color: item.fileType === 'pdf' ? '#b91c1c' : '#3730a3'
                                            }}>
                                                {item.fileType === 'pdf' ? '📄 PDF' : '🖼️ IMG'}
                                            </span>
                                        </td>

                                        {/* Title & Description */}
                                        <td style={{ padding: '12px 14px' }}>
                                            <div style={{ fontWeight: '600', color: '#1e293b', fontSize: '14.5px', marginBottom: '3px' }}>
                                                {item.title}
                                            </div>
                                            {item.description && (
                                                <div style={{ fontSize: '12.5px', color: '#64748b', maxWidth: '420px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                    {item.description}
                                                </div>
                                            )}
                                        </td>

                                        {/* Date */}
                                        <td style={{ padding: '12px 14px', color: '#475569', fontWeight: '500', whiteSpace: 'nowrap' }}>
                                            📅 {item.date}
                                        </td>

                                        {/* Status */}
                                        <td style={{ padding: '12px 14px' }}>
                                            <span style={{
                                                display: 'inline-block',
                                                padding: '3px 9px',
                                                borderRadius: '12px',
                                                fontSize: '11px',
                                                fontWeight: '700',
                                                background: item.status === 'closed' ? '#f3f4f6' : '#dcfce7',
                                                color: item.status === 'closed' ? '#6b7280' : '#15803d'
                                            }}>
                                                {item.status === 'closed' ? 'අවසන්' : 'සක්‍රියයි'}
                                            </span>
                                        </td>

                                        {/* View File Button */}
                                        <td style={{ padding: '12px 14px' }}>
                                            {item.fileData ? (
                                                <button
                                                    type="button"
                                                    onClick={() => openFileInNewTab(item.fileData, item.fileType, item.title)}
                                                    style={{
                                                        background: '#f1f5f9',
                                                        color: '#0e633d',
                                                        border: '1px solid #cbd5e1',
                                                        padding: '5px 10px',
                                                        borderRadius: '6px',
                                                        fontSize: '12px',
                                                        fontWeight: '600',
                                                        cursor: 'pointer',
                                                        display: 'inline-flex',
                                                        alignItems: 'center',
                                                        gap: '4px'
                                                    }}
                                                >
                                                    <span>👁️</span> {item.fileType === 'pdf' ? 'බලන්න (PDF)' : 'බලන්න (IMG)'}
                                                </button>
                                            ) : (
                                                <span style={{ color: '#94a3b8', fontSize: '12px' }}>නොමැත</span>
                                            )}
                                        </td>

                                        {/* Actions */}
                                        <td style={{ padding: '12px 14px', textAlign: 'right', whiteSpace: 'nowrap' }}>
                                            <button
                                                type="button"
                                                onClick={() => handleEdit(item)}
                                                style={{
                                                    background: '#e0f2fe',
                                                    color: '#0284c7',
                                                    border: 'none',
                                                    padding: '6px 10px',
                                                    borderRadius: '6px',
                                                    fontSize: '12px',
                                                    fontWeight: '600',
                                                    cursor: 'pointer',
                                                    marginRight: '6px'
                                                }}
                                                title="සංස්කරණය කරන්න"
                                            >
                                                ✏️ Edit
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => handleDelete(item.id)}
                                                style={{
                                                    background: '#fee2e2',
                                                    color: '#ef4444',
                                                    border: 'none',
                                                    padding: '6px 10px',
                                                    borderRadius: '6px',
                                                    fontSize: '12px',
                                                    fontWeight: '600',
                                                    cursor: 'pointer'
                                                }}
                                                title="ඉවත් කරන්න"
                                            >
                                                🗑️ Delete
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
}
